import { useForgotPassword, useIsAuthenticated, useLogin } from "@refinedev/core";
import { useEffect, useState } from "react";
import { Navigate } from "react-router";

import {
  clearStaffAccessDenied,
  hasStaffAccessDenied,
  STAFF_ACCESS_DENIED_NOTICE,
} from "../auth/access-denied-notice";
import { getAdminPublicConfig } from "../config/public-config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { StatusMessage } from "../ui/status-message";
import { AuthField, AuthShell, PasswordInput } from "./auth-parts";

// The staff sign-in page. It draws the form and hands the answer of the auth provider to the person in words: whether a person may use the
// console is decided by the provider and the API, never here. Every failure is a message with an icon and words, in a live region.

export function LoginPage() {
  const config = getAdminPublicConfig();
  const { mutate: login, isPending } = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [resetRequested, setResetRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: forgotPassword, isPending: isResetting } = useForgotPassword();
  const { data: authentication } = useIsAuthenticated();
  // CHT-1628: a non-staff session was signed out on its way here. The marker is read without removing it (a repeated render must see the
  // same answer) and removed once the page is shown.
  const [accessDenied] = useState(() => hasStaffAccessDenied(window.localStorage));
  useEffect(() => {
    clearStaffAccessDenied(window.localStorage);
  }, []);

  if (authentication?.authenticated) return <Navigate to="/home" replace />;

  return (
    <AuthShell titleId="login-title" title="Staff Console">
      <p className="m-0 text-base leading-7 text-muted-foreground">
        Sign in with your Lafayette staff account. Staff access is verified by the API.
      </p>
      {accessDenied ? (
        <StatusMessage message={{ tone: "error", text: STAFF_ACCESS_DENIED_NOTICE }} />
      ) : null}
      {config.developmentAuthPlaceholderEnabled ? (
        <Button
          type="button"
          size="lg"
          className="w-full"
          loading={isPending}
          onClick={() => login({})}
        >
          {isPending ? "Entering…" : "Enter development shell"}
        </Button>
      ) : (
        <>
          <form
            className="grid gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              login(
                { email, password, mfaCode },
                {
                  // A provider that resolves { success: false } reaches onSuccess, not onError.
                  onSuccess: (result) => {
                    if (!result.success) {
                      setError(result.error?.message ?? "Sign in failed.");
                    }
                  },
                  onError: (loginError) =>
                    setError(loginError instanceof Error ? loginError.message : "Sign in failed."),
                },
              );
            }}
          >
            <AuthField id="login-email" label="Email">
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                className="rounded-md text-base md:text-base"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </AuthField>
            <AuthField id="login-password" label="Password">
              <PasswordInput
                id="login-password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                showLabel="Show password"
              />
            </AuthField>
            {error?.toLowerCase().includes("mfa") ? (
              <AuthField
                id="login-mfa"
                label="MFA code"
                hint="Enter the 6-digit code from your authenticator app."
              >
                <Input
                  id="login-mfa"
                  name="one-time-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="rounded-md text-base md:text-base"
                  aria-describedby="login-mfa-hint"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                />
              </AuthField>
            ) : null}
            {error ? <StatusMessage message={{ tone: "error", text: error }} /> : null}
            <Button type="submit" size="lg" className="w-full" loading={isPending}>
              {isPending ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="grid gap-3 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              className="text-base"
              disabled={isResetting || !email}
              onClick={() => {
                forgotPassword({ email }, { onSuccess: () => setResetRequested(true) });
              }}
            >
              {isResetting ? "Sending reset email…" : "Reset password"}
            </Button>
            {!email ? (
              <p className="m-0 text-sm leading-5 text-muted-foreground">
                Type your email above to ask for a reset email.
              </p>
            ) : null}
            {resetRequested ? (
              <StatusMessage
                message={{
                  tone: "success",
                  text: "If the account exists, a reset email has been sent.",
                }}
              />
            ) : null}
          </div>
        </>
      )}
    </AuthShell>
  );
}
